import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getMyRequests, setRequestPriority } from "../../api/agent";
import HelpLink, { GUIDE_URLS } from "@/components/HelpLink";
import RequestDetail from "./RequestDetail";
import PendingQuestions, { useQuestionsForMe } from "./PendingQuestions";

const PRIORITY_GROUPS = [
  { key: "P1", title: "🔴 Priorité 1 — bloquant" },
  { key: "P2", title: "🟠 Priorité 2 — important" },
  { key: "P3", title: "⚪ Priorité 3 — plus tard" },
  { key: null, title: "Sans priorité" },
];

const PRIORITY_RANK = { P1: 0, P2: 1, P3: 2 };

const STATUS_COLUMNS = [
  { key: "noted", title: "📥 Noté" },
  { key: "en_cours", title: "🔨 En cours" },
  { key: "livre", title: "✅ Livré" },
  { key: "plus_tard", title: "💤 Plus tard" },
];

// Le mail de livraison renvoie vers `/mes-demandes?demande=<id>`. On lit
// `window.location.search` plutôt qu'un hook de routeur : ce lien n'est jamais
// suivi depuis l'app, toujours par un chargement à froid depuis la boîte mail,
// la valeur n'a donc pas à être réactive. `RequestDetail` charge la demande par
// son id, elle s'ouvre donc même si la ligne n'est pas dans le scope affiché.
function getRequestedId() {
  try {
    return new URLSearchParams(window.location.search).get("demande") || null;
  } catch {
    return null;
  }
}

function getInitialView() {
  try {
    return window.localStorage.getItem("myRequestsView") === "list"
      ? "list"
      : "columns";
  } catch {
    return "columns";
  }
}

function capitalizePersona(persona) {
  if (!persona) return "";
  return persona.charAt(0).toUpperCase() + persona.slice(1);
}

function groupByPriority(requests) {
  return PRIORITY_GROUPS.map((group) => ({
    ...group,
    items: requests.filter((request) => {
      if (group.key === null) {
        return !["P1", "P2", "P3"].includes(request.priority);
      }
      return request.priority === group.key;
    }),
  })).filter((group) => group.items.length > 0);
}

// Doit rester d'accord avec `_priority_sort_key` (backend/agent/backlog_storage.py) :
// le backend trie au chargement, cette fonction rejoue le tri après un changement
// de priorité. Deux règles différentes feraient sauter les lignes d'une place au
// premier clic. Un test golden couvre chaque côté sur le même jeu.
function activityAt(request) {
  return Date.parse(request.last_activity_at || request.created_at || "") || 0;
}

export function sortByPriority(requests) {
  return [...requests].sort((left, right) => {
    const rankDifference = (
      (PRIORITY_RANK[left.priority] ?? 3) - (PRIORITY_RANK[right.priority] ?? 3)
    );
    if (rankDifference !== 0) return rankDifference;
    return activityAt(right) - activityAt(left);
  });
}

function PriorityBadge({ priority }) {
  if (!priority) return null;
  return (
    <span className="priority-badge text-xs px-2 py-1 rounded bg-orange-50 text-orange-700 whitespace-nowrap">
      {priority}
    </span>
  );
}

function PrioritySelector({ request, disabled, onChange }) {
  return (
    <select
      aria-label={`Priorité de ${request.title}`}
      value="unchanged"
      disabled={disabled}
      onChange={(event) => {
        const value = event.target.value;
        if (value !== "unchanged") onChange(value === "none" ? null : value);
      }}
      className="priority-selector w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 disabled:cursor-wait disabled:opacity-60"
    >
      <option value="unchanged">
        {request.priority ? "Modifier la priorité…" : "Poser une priorité…"}
      </option>
      <option value="P1">P1 — prioritaire</option>
      <option value="P2">P2</option>
      <option value="P3">P3</option>
      <option value="none">Aucune priorité</option>
    </select>
  );
}

export default function MyRequests({ token, canAccessDossiers = true }) {
  const navigate = useNavigate();
  const [scope, setScope] = useState("mine");
  const [isTeam, setIsTeam] = useState(false);
  const [canPrioritize, setCanPrioritize] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequestId, setSelectedRequestId] = useState(getRequestedId);
  const [updatingPriorities, setUpdatingPriorities] = useState(() => new Set());
  const [pendingQuestions, reloadPendingQuestions] = useQuestionsForMe(token);
  const [view, setView] = useState(getInitialView);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getMyRequests(token, scope)
      .then((d) => {
        if (cancelled) return;
        setRequests(d.requests || []);
        setIsTeam(!!d.is_team);
        setCanPrioritize(!!d.can_prioritize);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error(e);
        if (e?.response?.status === 403 && scope === "all") {
          setScope("mine");
          return;
        }
        toast.error("Impossible de charger tes demandes");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, scope]);

  const title = scope === "all" ? "Toutes les demandes" : "Mes demandes";
  const groupedRequests = scope === "all" ? groupByPriority(requests) : [];
  // La colonne ✅ Livré était retirée du scope « Toutes » : la demande dont on
  // venait de recevoir le mail d'annonce n'était pas à l'écran. Le backend n'y
  // renvoie que les livraisons récentes, la colonne ne gonfle donc pas.
  const statusColumns = STATUS_COLUMNS
    .map((column) => ({
      ...column,
      items: requests.filter((request) => request.status === column.key),
    }));

  function changeView(nextView) {
    setView(nextView);
    try {
      window.localStorage.setItem("myRequestsView", nextView);
    } catch {
      // Storage can be unavailable (for example in private browsing).
    }
  }

  function mergeRequestSummary(detail, { commented = false } = {}) {
    const lastComment = detail.comments?.[detail.comments.length - 1];
    setRequests((current) => sortByPriority(current.map((request) => (
      request.id === detail.id
        ? {
            ...request,
            comments_count: detail.comments
              ? detail.comments.length
              : request.comments_count,
            status_since: detail.status_since,
            priority: detail.priority,
            priority_by: detail.priority_by,
            priority_at: detail.priority_at,
            // Le badge dit « on t'a répondu » : il doit s'éteindre dès qu'on a
            // répondu à son tour, sans attendre un rechargement de la page.
            // Seul le chemin commentaire l'éteint — un changement de priorité
            // passe aussi par ici et ne répond à personne.
            ...(commented
              ? {
                  has_new_reply: false,
                  last_activity_at: lastComment?.at || request.last_activity_at,
                }
              : {}),
          }
        : request
    ))));
  }

  function updateRequestSummary(detail) {
    // Un commentaire vaut réponse : sans ce rechargement, la question resterait
    // affichée dans le bandeau alors qu'on vient d'y répondre.
    reloadPendingQuestions();
    mergeRequestSummary(detail, { commented: true });
  }

  async function changePriority(request, priority) {
    if (updatingPriorities.has(request.id)) return;
    setUpdatingPriorities((current) => new Set(current).add(request.id));
    try {
      const detail = await setRequestPriority(token, request.id, priority);
      mergeRequestSummary(detail);
    } catch (error) {
      console.error(error);
      toast.error("Impossible de modifier la priorité");
    } finally {
      setUpdatingPriorities((current) => {
        const next = new Set(current);
        next.delete(request.id);
        return next;
      });
    }
  }

  // Vert : l'orange est déjà pris par la priorité ET par le compteur de
  // commentaires, le bleu par le persona du demandeur. Un 4e badge orange se
  // serait fondu dans les trois autres.
  function newReplyBadge(request) {
    if (!request.has_new_reply) return null;
    return (
      <span className="request-new-reply text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 whitespace-nowrap">
        🆕 Réponse
      </span>
    );
  }

  function commentsBadge(request) {
    if (!request.comments_count) return null;
    return (
      <span className="text-xs px-2 py-1 rounded bg-orange-50 text-orange-700 whitespace-nowrap">
        💬 {request.comments_count}
      </span>
    );
  }

  return (
    <div
      className={`my-requests-page mx-auto p-6 ${
        view === "columns" ? "max-w-6xl" : "max-w-3xl"
      }`}
    >
      <PendingQuestions questions={pendingQuestions} onOpen={setSelectedRequestId} />
      <div className="my-requests-header mb-6">
        {/* Le mail de livraison amène ici, y compris un demandeur non-équipe :
            l'assistant lui répondrait 403. On le ramène au tableau de bord
            plutôt que de le laisser sans retour. */}
        <button
          onClick={() => navigate(isTeam || !canAccessDossiers ? "/assistant" : "/")}
          className="text-sm text-gray-600 hover:text-gray-900 mb-3"
        >
          {isTeam || !canAccessDossiers ? "← Assistant" : "← Tableau de bord"}
        </button>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold">{title}</h1>
            <p className="text-sm text-gray-600">
              {scope === "all"
                ? `${requests.length} demande${requests.length !== 1 ? "s" : ""}`
                : "Le suivi de ce que tu as signalé à l'assistant."}
            </p>
            {isTeam && (
              <p className="my-requests-hint mt-1 text-sm text-gray-500">
                Une question pour quelqu'un en particulier ? Dis-le à l'assistant :
                {" "}
                <span className="italic">« pose cette question à Vincent »</span>. Il
                la verra dans son bandeau « On attend ta réponse ».
              </p>
            )}
          </div>
          <div className="my-requests-actions flex flex-wrap items-center gap-2">
            <HelpLink url={GUIDE_URLS.assistant} />
            {isTeam && (
              <button
                type="button"
                onClick={() => navigate("/nouveautes")}
                className="my-requests-deliveries-link rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                ✨ Nouveautés
              </button>
            )}
            {isTeam && (
              <div className="my-requests-scope-toggle inline-flex rounded-lg bg-gray-100 p-1">
                <button
                  type="button"
                  onClick={() => setScope("mine")}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                    scope === "mine"
                      ? "bg-orange-600 text-white"
                      : "bg-gray-200 text-gray-800"
                  }`}
                >
                  Mes demandes
                </button>
                <button
                  type="button"
                  onClick={() => setScope("all")}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                    scope === "all"
                      ? "bg-orange-600 text-white"
                      : "bg-gray-200 text-gray-800"
                  }`}
                >
                  Toutes les demandes
                </button>
              </div>
            )}
            <div className="my-requests-view-toggle inline-flex rounded-lg bg-gray-100 p-1">
              <button
                type="button"
                aria-pressed={view === "list"}
                onClick={() => changeView("list")}
                className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                  view === "list"
                    ? "bg-orange-600 text-white"
                    : "bg-gray-200 text-gray-800"
                }`}
              >
                Liste
              </button>
              <button
                type="button"
                aria-pressed={view === "columns"}
                onClick={() => changeView("columns")}
                className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                  view === "columns"
                    ? "bg-orange-600 text-white"
                    : "bg-gray-200 text-gray-800"
                }`}
              >
                Colonnes
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading && <p className="text-gray-500">Chargement…</p>}
      {!loading && requests.length === 0 && (
        <p className="text-gray-500">
          Aucune demande pour l'instant. Signale un bug ou une idée à l'assistant !
        </p>
      )}

      {!loading && view === "columns" && requests.length > 0 && (
        <div
          className={`request-columns grid grid-cols-1 gap-4 md:grid-cols-2 ${
            scope === "all" ? "lg:grid-cols-3" : "lg:grid-cols-4"
          }`}
        >
          {statusColumns.map((column) => (
            <section
              key={column.key}
              data-status={column.key}
              className="request-column min-w-0 rounded-lg border bg-gray-50 p-3"
            >
              <h2 className="mb-3 text-sm font-semibold text-gray-700">
                {column.title} ({column.items.length})
              </h2>
              <div className="request-column-items space-y-3 md:max-h-[70vh] md:overflow-y-auto md:pr-1">
                {column.items.length === 0 ? (
                  <p className="py-3 text-center text-sm text-gray-400">Aucune</p>
                ) : (
                  column.items.map((r) => (
                    <div key={r.id} className="space-y-2">
                      <button
                        type="button"
                        onClick={() => setSelectedRequestId(r.id)}
                        className="request-column-card flex w-full min-w-0 flex-col items-start gap-3 rounded-lg border bg-white p-4 text-left hover:border-orange-300 hover:bg-orange-50"
                      >
                        <span className="request-title w-full min-w-0 break-words text-sm text-gray-800">
                          {r.title}
                        </span>
                        {/* `Boolean(...)` n'est pas décoratif : sans lui, une demande sans
                            priorité et sans commentaire fait valoir à la garde le NOMBRE 0
                            (null || false || 0), et React affiche « 0 » sous le titre. Le cas
                            visait exactement les demandes qu'on veut faire prioriser. */}
                        {Boolean(
                          (isTeam && r.priority) || scope === "all"
                          || r.comments_count || r.has_new_reply
                        ) && (
                          <span className="request-badges flex w-full flex-wrap items-center gap-2">
                            {isTeam && <PriorityBadge priority={r.priority} />}
                            {scope === "all" && (r.requesters || []).map((persona) => (
                              <span
                                key={persona}
                                className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 whitespace-nowrap"
                              >
                                {capitalizePersona(persona)}
                              </span>
                            ))}
                            {newReplyBadge(r)}
                            {commentsBadge(r)}
                          </span>
                        )}
                      </button>
                      {canPrioritize && (
                        <PrioritySelector
                          request={r}
                          disabled={updatingPriorities.has(r.id)}
                          onChange={(priority) => changePriority(r, priority)}
                        />
                      )}
                    </div>
                  ))
                )}
              </div>
            </section>
          ))}
        </div>
      )}

      {!loading && view === "list" && requests.length > 0 && scope === "mine" && (
        <ul className="space-y-3">
          {requests.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => setSelectedRequestId(r.id)}
                className="request-list-button flex w-full min-w-0 items-center justify-between gap-3 rounded-lg border bg-white p-4 text-left hover:border-orange-300 hover:bg-orange-50"
              >
                <span className="request-title min-w-0 text-sm text-gray-800">{r.title}</span>
                <span className="request-badges flex shrink-0 items-center gap-2">
                  {isTeam && <PriorityBadge priority={r.priority} />}
                  {newReplyBadge(r)}
                  {commentsBadge(r)}
                  <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 whitespace-nowrap">
                    {r.label}
                  </span>
                </span>
              </button>
              {canPrioritize && (
                <div className="mt-2">
                  <PrioritySelector
                    request={r}
                    disabled={updatingPriorities.has(r.id)}
                    onChange={(priority) => changePriority(r, priority)}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {!loading && view === "list" && requests.length > 0 && scope === "all" && (
        <div className="space-y-6">
          {groupedRequests.map((group) => (
            <section key={group.key || "none"}>
              <h2 className="mb-3 text-sm font-semibold text-gray-700">{group.title}</h2>
              <ul className="space-y-3">
                {group.items.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedRequestId(r.id)}
                      className="request-list-button flex w-full min-w-0 items-start justify-between gap-4 rounded-lg border bg-white p-4 text-left hover:border-orange-300 hover:bg-orange-50"
                    >
                      <span className="request-title min-w-0 text-sm text-gray-800">{r.title}</span>
                      <span className="request-badges flex shrink-0 flex-wrap justify-end gap-2">
                        {isTeam && <PriorityBadge priority={r.priority} />}
                        {(r.requesters || []).map((persona) => (
                          <span
                            key={persona}
                            className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 whitespace-nowrap"
                          >
                            {capitalizePersona(persona)}
                          </span>
                        ))}
                        {newReplyBadge(r)}
                        {commentsBadge(r)}
                        <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 whitespace-nowrap">
                          {r.label}
                        </span>
                      </span>
                    </button>
                    {canPrioritize && (
                      <div className="mt-2">
                        <PrioritySelector
                          request={r}
                          disabled={updatingPriorities.has(r.id)}
                          onChange={(priority) => changePriority(r, priority)}
                        />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {selectedRequestId && (
        <RequestDetail
          token={token}
          requestId={selectedRequestId}
          isTeam={isTeam}
          onClose={() => setSelectedRequestId(null)}
          onUpdated={updateRequestSummary}
        />
      )}
    </div>
  );
}
